import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { InputBaseComponentProps } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { green, lightBlue } from "@mui/material/colors";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import CurrencyInput from "_shared/money/currencies/_views/currency-input";
import { Currency as CurrencyType } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import React, { forwardRef } from "react";
import TextChange from "work/proposals/redlining/__diff/text-change";
import FeeRedline from "work/proposals/redlining/fee-schedule-redline/fee-redline/fee-redline";

const DiffContainer = styled("div")(() => ({
  width: "100%",
}));
// cspell:disable
const DiffResult = styled("span")<{
  isdeferred?: boolean;
  isnewlyadded?: boolean;
  change?: TextChange;
  resolved?: boolean;
}>(({ isdeferred, isnewlyadded, change, resolved, theme }) => ({
  backgroundColor: (function () {
    if (resolved) return "unset";
    if (change?.isAdded) return green[300];
  })(),
  color: (function () {
    if (isnewlyadded) return lightBlue[700];
    if (isdeferred && !isnewlyadded) return theme.palette.error.main;
    return theme.palette.text.primary;
  })(),
  textDecoration: (function () {
    if (change?.isRemoved) return "line-through";
  })(),
  textDecorationColor: (function () {
    if (change?.isRemoved && resolved) return lightBlue[700];
    if (change?.isRemoved) return theme.palette.error.main;
  })(),
}));
// cspell:enable
const TextContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  fontSize: "0.9em",
  height: "100%",
  width: "100%",
}));
const InputContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  "&.MuiInputBase-input": {
    height: "unset",
  },
}));
const ChangeContainer = styled("div")(() => ({
  fontSize: "0.875rem",
}));
const DiffHeader = styled(Typography)(({ theme }) => ({
  color: lightBlue[500],
  display: "inline-block",
  fontSize: "0.75rem",
  lineHeight: 1,
  minWidth: theme.spacing(13.5),
}));
const PerSymbol = styled("span")(({ theme }) => ({
  fontSize: "1.5em",
  margin: theme.spacing(0, 1),
}));
const FeeRate = styled("span")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
}));
const FixedFeeLabel = styled(FormControlLabel)(() => ({
  marginLeft: 0,
}));
const FeeInput = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(1),
}));

export interface DiffFeeProps extends InputBaseComponentProps {
  className?: string;
  feeRedline: FeeRedline | null;
  isNewlyAdded: boolean;
  showTextInput?: boolean;
  onFeeInputChange?: (fee: Fee) => void;
  onTextInputSubmit?: () => void;
  onTextInputCancel?: () => void;
}

const DiffFee = forwardRef<HTMLInputElement, DiffFeeProps>(
  function DiffFee(props, ref) {
    const {
      className,
      feeRedline,
      isNewlyAdded,
      showTextInput,
      onClick,
      onFeeInputChange,
      onTextInputSubmit,
      onTextInputCancel
    } = props;

    const [currentFeeRate, setCurrentFeeRate] = React.useState<Money | undefined>(
      feeRedline?.currentEntry?.rate ?? feeRedline?.revisedEntry?.rate ?? feeRedline?.originalEntry?.rate
    );
    const [currentFeeUnit, setCurrentFeeUnit] = React.useState<string>(
      feeRedline?.currentEntry?.unit ?? feeRedline?.revisedEntry?.unit ?? feeRedline?.originalEntry?.unit ?? ""
    );
    const [isFixedFee, setIsFixedFee] = React.useState<boolean>(
      (feeRedline?.currentEntry?.unit ?? feeRedline?.revisedEntry?.unit ?? feeRedline?.revisedEntry?.unit) === null
    );

    const handleFixedFeeChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setIsFixedFee((prevValue) => !prevValue);
      let updatedFee: Fee;
      if (event.target.checked) {
        updatedFee = Fee.create({ rate: currentFeeRate ?? Money.create({ amount: 0, currency: CurrencyType.USD }), unit: null });
      } else {
        updatedFee = Fee.create({ rate: currentFeeRate ?? Money.create({ amount: 0, currency: CurrencyType.USD }), unit: currentFeeUnit });
      }
      onFeeInputChange?.(updatedFee);
    };

    const handleFeeUnitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      let updatedFee: Fee;
      if (isFixedFee) {
        updatedFee = Fee.create({ rate: currentFeeRate ?? Money.create({ amount: 0, currency: CurrencyType.USD }), unit: null });
      } else {
        setCurrentFeeUnit(event.target.value);
        updatedFee = Fee.create({ rate: currentFeeRate ?? Money.create({ amount: 0, currency: CurrencyType.USD }), unit: event.target.value });
      }

      onFeeInputChange?.(updatedFee);
    };

    const handleFeeRateChange = (currency: CurrencyType, amount: number) => {
      setCurrentFeeRate(Money.create({ amount, currency: currency ?? CurrencyType.USD }));
      const updatedFee = Fee.create({
        rate: new Money(amount, currency ?? CurrencyType.USD),
        unit: currentFeeUnit,
      });
      onFeeInputChange?.(updatedFee);
    };

    const getChangeKey = (change: TextChange): string => {
      let changeType: string;
      if (change.isAdded) {
        changeType = "added";
      } else if (change.isRemoved) {
        changeType = "removed";
      } else {
        changeType = "unchanged";
      }
      return `${change?.diff.value}_${changeType}`;
    };

    const canSubmit: boolean =
      (currentFeeRate && isFixedFee) ||
      (currentFeeRate && currentFeeUnit && !isFixedFee) ||
      false;

    return (
      <TextContainer>
        <input ref={ref} type="hidden" />
        <InputContainer className={className}>
          {!showTextInput && (
            <DiffContainer onClick={onClick}>
              {feeRedline?.textChanges.map((change: TextChange) => {
                // cspell:disable
                return (
                  <DiffResult
                    key={getChangeKey(change)}
                    isdeferred={feeRedline.currentEntry === null}
                    isnewlyadded={isNewlyAdded}
                    change={change}
                    resolved={feeRedline.isResolved}
                  >
                    {change?.diff.value}
                  </DiffResult>
                );
                // cspell:enable
              })}
            </DiffContainer>
          )}
          {showTextInput && (
            <>
              <ChangeContainer>
                <DiffHeader variant="overline">previously:</DiffHeader>
                {feeRedline?.originalEntry?.toString() ?? "Deferred"}
              </ChangeContainer>
              <ChangeContainer>
                <DiffHeader variant="overline">your changes:</DiffHeader>
                {feeRedline?.textChanges.map((change: TextChange) => {
                  return (
                    // cspell:disable
                    <DiffResult
                      isdeferred={feeRedline?.currentEntry === null}
                      isnewlyadded={isNewlyAdded}
                      key={getChangeKey(change)}
                      change={change}
                      resolved
                    >
                      {change?.diff.value}
                    </DiffResult>
                    // cspell:enable
                  );
                })}
              </ChangeContainer>
              <FeeInput>
                <FeeRate>
                  <CurrencyInput
                    size="small"
                    label="Amount"
                    variant="standard"
                    value={currentFeeRate}
                    onChange={handleFeeRateChange}
                    onBlurCapture={() => {
                      onTextInputSubmit?.();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === KeyboardKey.Enter && canSubmit) {
                        onTextInputSubmit?.();
                      }
                      if (event.key === KeyboardKey.Escape) {
                        onTextInputCancel?.();
                      }
                    }}
                  />
                  {!isFixedFee && (
                    <>
                      <PerSymbol>/</PerSymbol>
                      <TextField
                        label="Unit"
                        size="small"
                        variant="standard"
                        style={{ minWidth: "50px" }}
                        value={currentFeeUnit}
                        onChange={handleFeeUnitChange}
                        onBlurCapture={() => {
                          onTextInputSubmit?.();
                        }}
                        onKeyDown={(event) => {
                          if (event.key === KeyboardKey.Enter && canSubmit) {
                            onTextInputSubmit?.();
                          }
                          if (event.key === KeyboardKey.Escape) {
                            onTextInputCancel?.();
                          }
                        }}
                      />
                    </>
                  )}
                </FeeRate>
                <FixedFeeLabel
                  label="Fixed"
                  labelPlacement="end"
                  control={
                    <Checkbox
                      size="small"
                      edge="start"
                      color="primary"
                      icon={<CheckBoxOutlineBlankIcon />}
                      checked={isFixedFee}
                      onChange={handleFixedFeeChange}
                      onKeyDown={(event) => {
                        if (event.key === KeyboardKey.Enter && canSubmit) {
                          onTextInputSubmit?.();
                        }
                        if (event.key === KeyboardKey.Escape) {
                          onTextInputCancel?.();
                        }
                      }}
                      onBlurCapture={() => {
                        onTextInputSubmit?.();
                      }}
                    />
                  }
                />
              </FeeInput>
            </>
          )}
        </InputContainer>
      </TextContainer>
    );
  }
);

export default DiffFee;
